import { keysOf, kmap } from './arrayHelpers';

// KeyBoard is reconstructed on every App render (its durable batching state lives
// on last_keystroke_ref so it survives). Takes a single named-props object — NOT a
// positional list — so adding/reordering a field can't silently shift bindings.
// `setWord` is App's `cacher` wrapper (commit the word ONLY through it, never a raw
// setter, or the /exhausted cache goes stale).
function KeyBoard({
  origin, apiTracker,
  game, _game, constraints, guessed, guessed_data, feature_unique,
  word, setWord, cursor, _cursor,
  tristate, _disabled, sharing, _sharing, feeding_back, _feeding_back,
  handle, _handle, share, _share, _vsize,
  clue, _clue, clue_requested, _clue_requested, fetching_clue, _fetching_clue, _suggestion,
  last_key, _last_key, fast,
  shadow_word, infeasibleLetters, pendingLetters, unrestrictedLetters, perms_result, perms_cache,
  strategic, relaxPositions,
  init_word, init_cursor, is_complete,
  end_game_feedback_ref, new_game_ref, last_keystroke_ref,
  _moveStats, _allGamesAvg, _pendingAnalytics, analyticsStartTime, analyticsPollTimer,
  fetchMoveAnalytics, consumePreemptedConstraints,
}) {
  if (! game.n) return;
  const non_words = game.game_moves.filter((e) => e.reply === "Z".repeat(game.n)).map((e) => e.guess.trim());
  // Keystroke batching: buffer events with timing deltas, send as batch.
  // State is stored on last_keystroke_ref.current so it persists across React renders
  // (KeyBoard constructor is called on every render).
  if (last_keystroke_ref && !last_keystroke_ref.current._ks) {
    last_keystroke_ref.current._ks = { buffer: [], inflight: null, last_time: 0 };
  }
  const ks = last_keystroke_ref ? last_keystroke_ref.current._ks : { buffer: [], inflight: null, last_time: 0 };
  const ks_drain = () => {
    if (ks.buffer.length === 0 || ks.inflight || ks.pausing_for_gamemove) return;
    const batch = ks.buffer.splice(0);
    const tail_ms = Date.now() - ks.last_time;
    const body = JSON.stringify({ events: batch, tail_ms });
    ks.inflight = (apiTracker
      ? apiTracker.trackedFetch(origin + "/keystroke", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body
        }, 'keystroke').then(() => {})
      : fetch(origin + "/keystroke", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body
        })
    ).catch(() => {}).finally(() => {
      ks.inflight = null;
      ks_drain(); // drain any events that arrived while inflight
    });
  };
  const send_keystroke = (event_code) => {
    const now = Date.now();
    const delta_ms = ks.last_time > 0 ? now - ks.last_time : 0;
    ks.last_time = now;
    ks.buffer.push({ event: event_code, delta_ms });
    ks_drain();
  };
  // Expose send_keystroke on the ref so App.js can emit strategic mode events (98/99).
  if (last_keystroke_ref) last_keystroke_ref.current._send_keystroke = send_keystroke;
  // Relax-model helpers. A position is "relaxed" when a strategic probe overrides it: the green
  // is unlocked there (blank, any letter, navigable). Every other position plays normally.
  const isRelaxed = (i) => relaxPositions.includes(i);
  // A green position that navigation skips. Relaxed slots are NOT skipped (they're open).
  function isEffectivelyGreen (i) {
    return constraints.pos[i].length === 1 && !isRelaxed(i);
  }
  function cursor_at_beg () {
    return constraints.pos.slice(0, cursor).every((e, i) => isEffectivelyGreen(i));
  }
  function next_cursor () {
    let rv = cursor + 1;
    const jump = constraints.pos.slice(cursor + 1).findIndex((e, idx) => !isEffectivelyGreen(cursor + 1 + idx));
    rv = cursor + (jump === -1 ? (game.n - cursor) : (jump + 1));
    if (rv === game.n) {
      const space_index = word.indexOf(' ');
      const dot_index = word.indexOf('.');
      if (space_index > -1 && dot_index > -1) {
        rv = Math.min(space_index, dot_index);
      } else if (space_index > -1) {
          rv = dot_index;
        rv = space_index
      } else if (dot_index > -1) {
        rv = dot_index;
      }
    }
    return rv;
  }
  function prev_cursor () {
    const jump = constraints.pos.slice(0, cursor).reverse().findIndex((e, idx) => !isEffectivelyGreen(cursor - 1 - idx));
    return cursor - (jump === -1 ? 0 : (jump + 1));
  }
  function is_green () {
    return constraints.pos[cursor].length === 1 && !isRelaxed(cursor);
  }
  function is_green_i (i) {
    return constraints.pos[i].length === 1 && !isRelaxed(i);
  }
  const merged_word = word.map((char, index) => char === ' ' ? shadow_word[index] : char);
  function constraints_violated () {
    const mc = constraints.min_counts;
    const ac = new Map();
    // Positional constraints — relaxed slots accept any letter (the green is unlocked there).
    const posOk = keysOf(merged_word).every(i => merged_word[i] == ' ' || isRelaxed(i) || constraints.pos[i].includes(merged_word[i]));
    if (posOk) {
      // Check min_counts and capped
      merged_word.map(e => { if (! ac.has(e)) ac.set(e, 0); ac.set(e, ac.get(e) + 1); });
      if (keysOf(mc).every(k => ac.has(k) && ac.get(k) >= mc.get(k))) {
        if (! constraints.capped.every(e => { return ac.has(e) === mc.has(e) && (! ac.has(e) || ac.get(e) === mc.get(e)); })) {
          return true;
        }
        return false;
      }
      return true;
    }
    // Positional violation. When a strategic probe is available submit is allowed anyway
    // (disabled_enter handles that); the server does the authoritative revealing rejection.
    if (!strategic) console.warn("unexpected 'pos error': please let us know", merged_word, constraints.pos);
    return true;
  }
  this.disabled_space = () => sharing || cursor === game.n;
  this.disabled_delete = () => {
    if (sharing || cursor === game.n || word[cursor] === ' ') return true;
    return is_green();  // is_green() is false at relaxed slots, so delete is allowed there
  };
  this.disabled_backspace = () => sharing || cursor_at_beg();
  this.disabled_escape = () => {
    if (feeding_back || sharing) return false;
    return cursor_at_beg() && word.every((e, i) => e === ' ' || (constraints.pos[i].length === 1 && e === constraints.pos[i][0]));
  };
  this.disabled_arrow_left = () => sharing || cursor_at_beg();
  this.disabled_arrow_right = () => sharing || cursor === game.n;
  this.disabled_digit = (e) => { if (sharing) return true; if (e === '$') return false; let i = Math.max(0, parseInt(e) - 1); return isEffectivelyGreen(i); }
  this.disabled_letter = (k) => {
    if (sharing || cursor === game.n) return true;
    if (isRelaxed(cursor)) return false;  // relaxed slot: any letter allowed (probe)
    return !constraints.pos[cursor].includes(k) || is_green();
  };
  // Analytics (vocab_grade, quartile_words, etc.) are purely informational — don't gate submission on them.
  // The server validates authoritatively via stab_matches.
  this.disabled_question = () => sharing || (clue !== "" && clue_requested) || (!is_complete() && !this.disabled_enter());
  this.disabled_enter = () => {
    if (sharing || is_complete()) return true;
    if (merged_word.indexOf(' ') > -1 || merged_word.indexOf('.') > -1) return true;
    if (non_words.includes(merged_word.join('')) || (feature_unique && guessed_data !== undefined)) return true;
    // Strategic probe: allow submitting a constraint-violating word. The server does the
    // authoritative check (accept iff it's a real strategic word, else a revealing rejection).
    if (strategic) return false;
    return constraints_violated();
  };
  this.disabled_stab = () => sharing || is_complete() || word.indexOf(' ') == -1 || word.join('').trim().indexOf(' ') > -1 || word.join('').trim().length === 0 || word.indexOf('.') > -1 || constraints.pos.every((e, i) => e.length === 1 || word[i] === ' ') || non_words.includes(word.join('').trim()) || perms_result !== null && perms_result.count === 0 || feature_unique && guessed_data !== undefined;
  this.letter_class = (k) => {
    const is_inactive_class = this.disabled_letter(k) ? "disabled " : ""
    if (isRelaxed(cursor)) {
      // Relaxed slot: every letter is typeable and we give NO strategic hints (no feasibility
      // colors — those would leak which probes are good). Show the slot's own (green) letter
      // normally, and all the other now-typeable letters in one neutral "not so dimmed" style
      // so the player can see they may type them.
      if (sharing || cursor === game.n) return "nowhere";
      return constraints.pos[cursor].includes(k) ? "maybe_here" : "relaxed";
    }
    if (sharing || ! constraints.min_counts.get(k) && constraints.capped.includes(k)) {
      return is_inactive_class + "nowhere";
    } else if (cursor === game.n || ! constraints.pos[cursor].includes(k)) {
      return is_inactive_class + "not_here";
    } else if (! constraints.pos[cursor].length === 1) {
      return is_inactive_class + "sure_here";
    } else if (infeasibleLetters.includes(k)) {
      return is_inactive_class + "infeasible";
    } else if (pendingLetters.includes(k)) {
      return is_inactive_class + "pending";
    } else if (!unrestrictedLetters.includes(k)) {
      return is_inactive_class + "pended";
    } else {
      return is_inactive_class + "maybe_here";
    }
  }
  this.space = () => { if (this.disabled_space()) return;
    word[cursor] = ' ';
    setWord(Array.from(word));
    _cursor(next_cursor());
    send_keystroke(32);
  }
  this.delete = () => { if (this.disabled_delete()) return;
    word[cursor] = ' ';
    setWord(Array.from(word));
    send_keystroke(28);
  }
  this.escape = () => { if (this.disabled_escape()) return;
    if (sharing) {
      _sharing(false);
      return;
    }
    if (feeding_back) {
      _feeding_back(false);
      return;
    }
    kmap(word, i => word[i] = ' ');
    // Re-fill greens, but leave relaxed slots blank (they're unlocked for a probe). The server's
    // escape blanks all active+relaxed slots and keeps non-relaxed greens filled — same result.
    kmap(constraints.pos, i => { if (constraints.pos[i].length === 1 && !isRelaxed(i)) word[i] = constraints.pos[i][0]; });
    setWord(Array.from(word));
    // Cursor → first non-green-or-relaxed slot = min(active ∪ relax), matching the server.
    let cursor_ = init_cursor(constraints.pos);
    for (let i = 0; i < game.n; i++) { if (!isEffectivelyGreen(i)) { cursor_ = i; break; } }
    _cursor(cursor_);
    send_keystroke(30);
  }
  this.backspace = () => { if (this.disabled_backspace()) return;
    const cursor_ = prev_cursor();
    word[cursor_] = ' ';
    setWord(Array.from(word));
    _cursor(cursor_);
    send_keystroke(29);
  }
  this.arrow_left = () => { if (this.disabled_arrow_left()) return;
    const target_l = prev_cursor();
    _cursor(target_l);
    send_keystroke(100 + target_l);
  }
  this.arrow_right = () => { if (this.disabled_arrow_right()) return;
    const target_r = next_cursor();
    console.log(`arrow_right: cursor=${cursor} -> ${target_r}, strategic=${strategic}, relax=[${relaxPositions.join(',')}]`);
    _cursor(target_r);
    send_keystroke(100 + target_r);
  }
  this.tap = (i) => {
    if (sharing) return;
    if (i === cursor) return;           // tap to current position is a no-op (server rejects it)
    if (isEffectivelyGreen(i)) return;  // can't land on a (non-relaxed) green; relaxed slots are tappable
    _cursor(i);
    send_keystroke(100 + i);
  }
  this.digit_dollar = (k, enabled_cb = () => {}) => { if (this.disabled_digit(k)) return;
    enabled_cb();
    if (k === '$') {
      _cursor(game.n);
      send_keystroke(100 + game.n);
      return;
    }
    let cursor_ = parseInt(k);
    if (cursor_ > game.n) cursor_ = game.n + 1;
    if (cursor_ > 0) cursor_ -= 1;
    while (cursor_ < game.n && isEffectivelyGreen(cursor_)) ++cursor_;
    _cursor(cursor_);
    send_keystroke(100 + cursor_);
  }
  this.letter = (k, cb = () => {}, physical = true) => {
    const remaining = fast - (Date.now() - last_key.t);
    if (physical && remaining > 0) {
      if (last_key.disabled) {
        _last_key({ t: Date.now(), cursor: last_key.cursor, disabled: true, flash: false, remaining: remaining });
        return;
      }
      if (last_key.cursor < game.n - 1) {
        if (cursor != last_key.cursor + 1) {
          if (constraints.pos[last_key.cursor + 1].length === 1 && !relaxPositions.includes(last_key.cursor + 1)) {
            if (k === constraints.pos[last_key.cursor + 1][0]) {
              _last_key({ t: Date.now(), cursor: last_key.cursor + 1, disabled: false, flash: true, remaining: remaining });
              return;
            // } else if (! constraints.pos[cursor].includes(k)) {
              // _last_key({ t: Date.now(), cursor: last_key.cursor, disabled: true, flash: false, remaining: remaining });
              // return;
            } else if (last_key.flash) {
              _last_key({ t: Date.now(), cursor: last_key.cursor + 1, disabled: true, flash: false, flash_red: true, remaining: remaining });
              return;
            }
          }
        }
      }
    }
    _last_key({ t: Date.now(), cursor: cursor, disabled: true, flash: false, remaining: remaining });
    if (this.disabled_letter(k)) return;
    _last_key({ t: Date.now(), cursor: cursor, disabled: false, flash: false, remaining: remaining });
    if (k >= 'a' && k <= 'z' || k === '.') {
      word[cursor] = k;
      setWord(Array.from(word));
      _cursor(next_cursor());
      // Suggestion clearing is handled by the fetch cycle now, so the old one can fade out gracefully.
      if (k !== '.') send_keystroke(k.charCodeAt(0) - 96); else send_keystroke(27);
      cb();
    }
  }
  const make_move = (async (move_word) => {
      if (tristate.current === 1) {
        console.log("found tristate 1");
      } else if (tristate.current === 2) {
        console.log("fetching");
        tristate.current = 1;
        _disabled(true);
        _suggestion("");
        // Do NOT clear strategic/relax here: a rejected probe (revealing rejection) returns 502
        // and must keep the player in strategic mode, aligned with the server (which still has the
        // draft as strategic). On a SUCCESSFUL move, fetchMoveAnalytics('move') clears it.
        
        ks.pausing_for_gamemove = true;
        
        try {
          // Wait only for the in-flight /keystroke call (if any).
          // Do NOT drain the buffer — any remaining events will be piggybacked.
          if (ks.inflight) await ks.inflight;
          
          // Steal any buffered keystrokes that haven't been sent yet.
          let gamemoveBody;
          let pending = [];
          if (ks.buffer.length > 0) {
            pending = ks.buffer.splice(0);
            const tail_ms = Date.now() - ks.last_time;
            gamemoveBody = JSON.stringify({
              guess: move_word,
              pending_keystrokes: { events: pending, tail_ms }
            });
          } else {
            gamemoveBody = move_word;
          }

          const trackedResult = apiTracker
            ? await apiTracker.trackedFetch(origin + "/gamemove", { method: "POST", credentials: "include", body: gamemoveBody }, 'gamemove')
            : { data: await (await fetch(origin + "/gamemove", { method: "POST", credentials: "include", body: gamemoveBody })).json() };
          const o = trackedResult.data?.o;
          if (!o) {
            // Rejected move: the server rolled back, including the piggybacked keystrokes. Put
            // them back so the next drain records them — otherwise the server's draft falls
            // behind the on-screen word and every later submit fails the keystroke-log check.
            if (trackedResult.response && pending.length > 0) ks.buffer.unshift(...pending);
            throw new Error('gamemove returned no data');
          }
          await new Promise(r => setTimeout(r, 9));
          perms_cache.current.clear();
          _game(o.game);
          _clue(o.game.clue);
          const consumed = consumePreemptedConstraints(o.pos, Object.entries(o.min_counts), o.capped, true);
          _vsize(o.vsize);
          _handle(o.handle);
          _share(o.share);
          setWord(init_word(consumed.pos), true);
          _cursor(init_cursor(consumed.pos));
          const last_move = o.game.game_moves[o.game.game_moves.length - 1];
          _moveStats({ move_x_k: last_move.move_x_k || 0, word_rank: last_move.word_rank || 0, word_count: last_move.word_count || 0, word_quartiles: last_move.word_quartiles || [], next_word_count: o.next_word_count || -1, vocab_actual: last_move.vocab_actual || 0, vocab_best: last_move.vocab_best || 0, vocab_worst: last_move.vocab_worst || 0 });
          // Seed pending analytics for the new move — gate enter/stab until fetched
          const reply = last_move.reply;
          const seedPending = [];
          if (reply !== 'Z'.repeat(game.n) && reply !== 'G'.repeat(game.n)) {
            seedPending.push('vocab_grade', 'quartile_words', 'minimax_grade');
            if (!consumed.hasPreempted) {
              seedPending.push('optimized_constraints');
            }
          }
          _pendingAnalytics(seedPending);
          // Clear stale inflight gate and poll timer from previous move
          if (analyticsPollTimer) { clearInterval(analyticsPollTimer.current); analyticsPollTimer.current = null; }
          // Kick off analytics polling for the new move
          if (analyticsStartTime) analyticsStartTime.current = Date.now();
          if (fetchMoveAnalytics) fetchMoveAnalytics('move');
          if (o.all_games_avg != null) _allGamesAvg(o.all_games_avg);
          if (last_move.reply != "Z".repeat(game.n)) {
            if (guessed.hasOwnProperty(last_move.guess.trim())) {
              guessed[last_move.guess.trim()].rep += 1;
            } else {
              guessed[last_move.guess.trim()] = { seq: last_move.seq, rep: 1 }
            }
          }
          setTimeout(() => {
            if (end_game_feedback_ref.current) { end_game_feedback_ref.current.focus(); }
            else if (new_game_ref.current) { new_game_ref.current.focus(); }
          }, 34);
        } catch (error) {
          console.log("error");
          await new Promise(r => setTimeout(r, 9));
          if (error.code === error.ABORT_ERR) {
            console.error("aborted fetch");
          } else {
            console.log(error);
          }
        } finally {
          tristate.current = 2;
          _disabled(false);
          ks.pausing_for_gamemove = false;
          ks_drain();
        }
      } else {
        console.log("found tristate 0");
      }
    })
  this.question = (async () => { if (this.disabled_question()) return;
    if (fetching_clue) return;
    try {
      _fetching_clue(true);
      const trackedResult = apiTracker
        ? await apiTracker.trackedFetch(origin + "/clue", { method: "POST", credentials: "include", body: "" }, 'clue')
        : { data: await (await fetch(origin + "/clue", { method: "POST", credentials: "include", body: "" })).json() };
      const obj = trackedResult.data?.o;
      if (typeof obj === 'string') {
        _clue(obj);
        _clue_requested(true);
      } else if (obj) {
        _clue(obj.text);
        _clue_requested(obj.requested);
      }
    } catch (error) {
      console.log("error");
    } finally {
      _fetching_clue(false);
    }
  })
  this.suggest = (async (_suggestion, _fetching_suggestion) => { if (is_complete()) return;
    try {
      _fetching_suggestion(true);
      const wordStr = word.join("");
      const trackedResult = apiTracker
        ? await apiTracker.trackedFetch(origin + "/suggest", { method: "POST", credentials: "include", body: wordStr }, 'suggest')
        : { data: await (await fetch(origin + "/suggest", { method: "POST", credentials: "include", body: wordStr })).json() };
      const resp = trackedResult.data;
      const words = resp?.o?.words || resp?.words || [];
      _suggestion(words.length > 0 ? words[0] : "");
    } catch (error) {
      console.log("suggest error", error);
    } finally {
      _fetching_suggestion(false);
    }
  })
  this.fill_word = (w, submit = false) => {
    if (sharing || is_complete()) return;
    if (!w || typeof w !== 'string') return;
    const clean = w.trim().toLowerCase();
    if (clean.length !== game.n) return;

    send_keystroke(30);

    kmap(word, i => word[i] = ' ');
    kmap(constraints.pos, i => {
      if (isEffectivelyGreen(i)) {
        word[i] = constraints.pos[i][0];
      }
    });

    for (let i = 0; i < game.n; i++) {
      if (!isEffectivelyGreen(i)) {
        const ch = clean[i];
        word[i] = ch;
        if (ch >= 'a' && ch <= 'z') {
          send_keystroke(ch.charCodeAt(0) - 96);
        } else if (ch === '.') {
          send_keystroke(27);
        }
      }
    }

    setWord(Array.from(word));
    _cursor(game.n);

    if (submit) {
      return make_move(clean);
    }
  };
  this.fill_and_submit = (w) => this.fill_word(w, true);
  this.enter = () => { if (this.disabled_enter()) return;
    return make_move(merged_word.join(""));
  }
  this.stab = () => { if (this.disabled_stab()) return;
    return make_move(word.join(""));
  }
}

function cx (msg) { console.log(msg); }
function cl (msg) { }

export default KeyBoard;

// todo
//   fast typing support for touch as well as keyboard
//   case of words finished

